import { type FC, useState, useEffect, useRef } from 'react';
import { useIntl } from 'dibs-react-intl';
import serverVars from 'server-vars';
import classnames from 'classnames';
import CircleCheck from 'dibs-icons/exports/legacy/CircleCheck.ts';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle.ts';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { Toast } from 'dibs-toaster/exports/Toast.ts';

import { copyMap, type ToastAction, type ToastStatus, REFRESH_METADATA } from './types.ts';

import styles from './ActionToast.scss';

const isMobile = serverVars.get('settings.isMobile');

export type ActionToastProps = {
    /**
     * an action toast will display if it is active and has not been
     * previously dismissed with the current status.
     */
    isActive?: boolean;
    onClose?: (() => void) | null;
    status: ToastStatus | null;
    action: ToastAction;
    affectedEditions?: number;
};

const ActionToast: FC<ActionToastProps> = ({
    isActive,
    onClose,
    status,
    action,
    affectedEditions,
}) => {
    const { formatMessage } = useIntl();
    const [isOpen, setIsOpen] = useState(isActive);
    const closedOnTypeRef = useRef<typeof status | null>(null);

    useEffect(() => {
        // only reopen toast if the status changes. the working assumption here
        // is that a user wants to ignore messages of a given status if they
        // have dismissed an <action>-toast with that status.
        if (isActive && status !== closedOnTypeRef.current) {
            setIsOpen(true);
        } else if (!isActive) {
            closedOnTypeRef.current = null;
            if (isOpen) {
                setIsOpen(false);
            }
        }
    }, [isActive, isOpen, status]);

    if (!isActive || !status) {
        closedOnTypeRef.current = null;
        return null;
    }

    const mappedTitle = copyMap[action][`${status}Title`];
    // prefer toast-specific messaging
    const mappedBody = copyMap[action][`${status}Toast`] || copyMap[action][status];
    const title = mappedTitle ? formatMessage(mappedTitle) : '';
    const body = mappedBody
        ? formatMessage(mappedBody, { ...(affectedEditions ? { affectedEditions } : {}) })
        : '';

    return (
        <Toast
            type="success"
            position={isMobile ? 'bottomCenter' : 'topRight'}
            isVisible={isOpen}
            onClose={() => {
                closedOnTypeRef.current = status;
                onClose?.();
                setIsOpen(false);
            }}
        >
            <div className={styles.title}>
                {status === 'error' && (
                    <ExclamationCircle className={classnames(styles.error, styles.icon)} />
                )}
                {status === 'success' && <CircleCheck className={styles.icon} />}
                {status === 'loading' && <Spinner containerClass={styles.icon} black />}
                {status === 'inProgress' && <Spinner containerClass={styles.icon} black />}
                {action === REFRESH_METADATA && status === 'inflight' && (
                    <Spinner containerClass={styles.icon} black />
                )}
                {title}
            </div>
            <p className={styles.body}>{body}</p>
        </Toast>
    );
};

export default ActionToast;
